import { normalizeUploadUrls } from '../utils/uploadUrls';
import API_BASE_URL from '../config/apiBaseUrl';

export const authStorage = {
  getToken: () => localStorage.getItem('admin_token'),
  setToken: (token) => localStorage.setItem('admin_token', token),
  clearToken: () => localStorage.removeItem('admin_token'),
  getUser: () => {
    const raw = localStorage.getItem('admin_user');
    return raw ? JSON.parse(raw) : null;
  },
  setUser: (user) => localStorage.setItem('admin_user', JSON.stringify(user)),
  clearUser: () => localStorage.removeItem('admin_user'),
  clearSession: () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
  },
};

let unauthorizedHandler = null;

export function setUnauthorizedHandler(handler) {
  unauthorizedHandler = handler;
}

export function getApiData(payload) {
  return payload?.data ?? payload?.Data ?? null;
}

export function normalizeAuthUser(raw) {
  if (!raw) return null;
  return {
    id: raw.id ?? raw.Id,
    fullName: raw.fullName ?? raw.FullName ?? '',
    email: raw.email ?? raw.Email ?? '',
    role: raw.role ?? raw.Role ?? '',
    avatarUrl: raw.avatarUrl ?? raw.AvatarUrl ?? null,
  };
}

export function normalizeLoginData(raw) {
  if (!raw) return null;

  const otpSessionId = raw.otpSessionId ?? raw.OtpSessionId ?? '';
  const requiresOtp = raw.requiresOtp ?? raw.RequiresOtp ?? Boolean(otpSessionId);
  const user = raw.user ?? raw.User;

  return {
    requiresOtp,
    otpSessionId,
    otpExpiresInSeconds: raw.otpExpiresInSeconds ?? raw.OtpExpiresInSeconds,
    maskedEmail: raw.maskedEmail ?? raw.MaskedEmail ?? '',
    token: raw.token ?? raw.Token ?? '',
    expiresAt: raw.expiresAt ?? raw.ExpiresAt,
    user: normalizeAuthUser(user),
  };
}

async function request(path, { method = 'GET', body, auth = true } = {}) {
  const headers = {
    'Content-Type': 'application/json',
  };

  if (auth) {
    const token = authStorage.getToken();
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
  }

  let response;
  try {
    response = await fetch(`${API_BASE_URL}${path}`, {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
    });
  } catch {
    throw new Error(
      `Cannot reach backend${API_BASE_URL ? ` at ${API_BASE_URL}` : ''}. Make sure the backend site is running and reachable.`,
    );
  }

  let payload = null;
  try {
    payload = await response.json();
  } catch {
    payload = null;
  }

  if (!response.ok) {
    if (response.status === 401 && auth) {
      unauthorizedHandler?.();
    }
    const validationErrors = Array.isArray(payload?.errors)
      ? payload.errors
      : payload?.errors && typeof payload.errors === 'object'
        ? Object.values(payload.errors).flat()
        : [];
    const message = payload?.message
      || validationErrors[0]
      || `Request failed with status ${response.status}`;
    throw new Error(message);
  }

  return normalizeUploadUrls(payload, API_BASE_URL);
}

export const api = {
  login: (email, password) =>
    request('/api/auth/login', {
      method: 'POST',
      body: { email, password },
      auth: false,
    }),
  verifyOtp: (otpSessionId, code) =>
    request('/api/auth/verify-otp', {
      method: 'POST',
      body: { otpSessionId, code },
      auth: false,
    }),
  resendOtp: (otpSessionId) =>
    request('/api/auth/resend-otp', {
      method: 'POST',
      body: { otpSessionId },
      auth: false,
    }),
  logout: () =>
    request('/api/auth/logout', {
      method: 'POST',
    }),
  me: () => request('/api/auth/me'),
  updateProfile: (data) =>
    request('/api/auth/profile', {
      method: 'PUT',
      body: data,
    }),
  dashboard: () => request('/api/dashboard'),
  visitorAnalytics: () => request('/api/analytics/visitors'),
  getWebsiteSections: () => request('/api/website-sections'),
  updateWebsiteSections: (data) =>
    request('/api/website-sections', {
      method: 'PUT',
      body: data,
    }),
  list: (endpoint) => request(endpoint),
  create: (endpoint, data) =>
    request(endpoint, {
      method: 'POST',
      body: data,
    }),
  update: (endpoint, id, data) =>
    request(`${endpoint}/${id}`, {
      method: 'PUT',
      body: data,
    }),
  remove: (endpoint, id) =>
    request(`${endpoint}/${id}`, {
      method: 'DELETE',
    }),
  searchEmployees: (q) =>
    request(`/api/admin/employee-verifications/erp-search?q=${encodeURIComponent(q)}`),
  listEmployeeVerifications: () => request('/api/admin/employee-verifications'),
  getEmployeeVerification: (employeeId) =>
    request(`/api/admin/employee-verifications/${encodeURIComponent(employeeId)}`),
  createEmployeeVerification: (employeeId) =>
    request('/api/admin/employee-verifications', { method: 'POST', body: { employeeId } }),
  approveEmployeeVerification: (employeeId) =>
    request(`/api/admin/employee-verifications/${encodeURIComponent(employeeId)}/approve`, { method: 'POST' }),
  rejectEmployeeVerification: (employeeId, reason) =>
    request(`/api/admin/employee-verifications/${encodeURIComponent(employeeId)}/reject`, {
      method: 'POST',
      body: { reason },
    }),
  deleteEmployeeVerification: (employeeId) =>
    request(`/api/admin/employee-verifications/${encodeURIComponent(employeeId)}`, { method: 'DELETE' }),
  revokeEmployeeVerification: (employeeId) =>
    request(`/api/admin/employee-verifications/${encodeURIComponent(employeeId)}/revoke`, { method: 'POST' }),
  uploadEmployeeCards: async (employeeId, { front, back }) => {
    const token = authStorage.getToken();
    const formData = new FormData();
    if (front) formData.append('front', front);
    if (back) formData.append('back', back);

    const response = await fetch(
      `${API_BASE_URL}/api/admin/employee-verifications/${encodeURIComponent(employeeId)}/cards`,
      {
        method: 'PUT',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
      },
    );

    let payload = null;
    try {
      payload = await response.json();
    } catch {
      payload = null;
    }

    if (!response.ok) {
      if (response.status === 401) {
        unauthorizedHandler?.();
      }
      const message = payload?.errors?.[0] || payload?.message || `Upload failed with status ${response.status}`;
      throw new Error(message);
    }

    return payload;
  },
  // Card images need the bearer token, so they are fetched as blobs for <img src>.
  fetchEmployeeCardBlobUrl: async (employeeId, side) => {
    const token = authStorage.getToken();
    const response = await fetch(
      `${API_BASE_URL}/api/admin/employee-verifications/${encodeURIComponent(employeeId)}/card/${side}`,
      { headers: token ? { Authorization: `Bearer ${token}` } : {} },
    );
    if (!response.ok) return null;
    return URL.createObjectURL(await response.blob());
  },
  uploadImage: async (file) => {
    const token = authStorage.getToken();
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(`${API_BASE_URL}/api/upload/image`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    });

    let payload = null;
    try {
      payload = await response.json();
    } catch {
      payload = null;
    }

    if (!response.ok) {
      if (response.status === 401) {
        unauthorizedHandler?.();
      }
      const message = payload?.message || `Upload failed with status ${response.status}`;
      throw new Error(message);
    }

    return normalizeUploadUrls(payload.data, API_BASE_URL);
  },
};

export { API_BASE_URL };
